import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import type { GraphCommandEvents } from 'components/Graph/model/GraphCommandEvents';

interface AddGraphCommandEventListenerCommand<
  TEventName extends keyof GraphCommandEvents = keyof GraphCommandEvents,
> {
  eventType: TEventName;
  listener: GraphCommandEvents[TEventName];
}

/**
 * Add event listener for graph command events
 *
 * ### Command with generic argument
 *
 * The command object cannot be generic out of the box - we would need to use higher-order generics
 * on the dispatch function, which isn't supported by typescript. Therefore we cannot ensure `TEventName`
 * won't infer just to full union `keyof GraphCommandEvents` and allow mixing event type with wrong
 * listener. But there is a trick - we create a generic factory function. When this function is called
 * with literally specified eventType and listener, it correctly reports error.
 *
 * Usage:
 * ```
 * dispatch(...addGraphCommandEventListener('someEvent', (e: SomeEvent) => ...));
 * ```
 */
export const addGraphCommandEventListener = <
  TEventName extends keyof GraphCommandEvents,
>(
  eventType: TEventName,
  listener: GraphCommandEvents[TEventName],
): [
  GraphCommandFactory<AddGraphCommandEventListenerCommand>,
  AddGraphCommandEventListenerCommand,
] => [
  (cmdContext) => (command) => {
    cmdContext.eventEmitter.addListener(command.eventType, command.listener);
  },
  { eventType, listener },
];
